import React, { useEffect, useRef, useState } from 'react';

import { Flex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Content as EditorFileContent } from '@tiptap/react';
import { AxiosError } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { ResourceViewerWrapper } from 'components/ResourceViewerWrapper/ResourceViewerWrapper';
import { EditorRef, SmartEditor } from 'pages/SmartEditor/SmartEditor';
import { EditorHeader } from 'pages/SmartEditor/SmartEditorHeader/SmartEditorHeader';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { MAX_TOTAL_BYTE_SIZE } from 'shared/constants/general-constants';
import { DOWNLOAD_FILE_QUERY_KEY, RESOURCE_QUERY_KEY, RESOURCE_RAW_TEXT_QUERY_KEY } from 'shared/constants/query-keys';
import { Resource, UpdateResourceFileDto } from 'shared/interfaces/Resource';
import { createSmartEditorFile } from 'shared/utils/create-smart-editor-file';

export function SmartEditorPage() {
  const { t } = useTranslation();
  const { resourceId } = useParams();
  const queryClient = useQueryClient();

  const editorRef = useRef<EditorRef>(null);
  const [resource, setResource] = useState<Resource | null>(null);
  const [resourceContent, setResourceContent] = useState<EditorFileContent | null>(null);
  const [isPendingChanges, setPendingChanges] = useState(false);

  const fileNameForm = useForm({
    initialValues: { fileName: '' },
    validate: {
      fileName: (value) => (value.length > 255 ? t('ERROR.FILE_NAME_TOO_LONG') : null)
    }
  });

  // Handle unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isPendingChanges) {
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPendingChanges]);

  if (!resourceId) return <ErrorAlert />;

  // Query to fetch resource content
  const downloadResourceContent = useQuery({
    queryKey: [DOWNLOAD_FILE_QUERY_KEY, { resourceId }],
    queryFn: async () => {
      const binary = await ResourceService.downloadResourceFile(resourceId);
      const text = new TextDecoder().decode(binary);
      const json = JSON.parse(text);
      setResourceContent(json);
      return json;
    }
  });

  const resourceInfoQuery = useQuery({
    queryKey: [RESOURCE_QUERY_KEY, { resourceId }],
    queryFn: () => ResourceService.getResourceInfo(resourceId),
    onSuccess: (resource) => {
      fileNameForm.setFieldValue('fileName', resource.fileName);
      setResource(resource);
    }
  });

  const updateResourceMutation = useMutation({
    mutationFn: (updateResourceDto: UpdateResourceFileDto) => ResourceService.updateResourceFile(updateResourceDto),
    onSuccess: () => {
      setPendingChanges(false);
    },
    onError: (error: AxiosError) => {
      NotificationService.showResult(false, t('FEED_BACK.SMART_EDITOR.UPDATE.FAILURE.UNKNOWN'), error);
    }
  });

  const handleSaveChanges = () => {
    const normalizedFileName = fileNameForm.values.fileName.trim() ? fileNameForm.values.fileName : 'Untitled';

    // Get editor content and validate
    const editorContent = editorRef.current?.exportEditorContent();
    if (!editorContent) {
      NotificationService.showResult(false, t('FEED_BACK.SMART_EDITOR.UPDATE.FAILURE.UNKNOWN'));
      return;
    }

    // Create file and check size
    const file = createSmartEditorFile(normalizedFileName, editorContent);
    if (file.size > MAX_TOTAL_BYTE_SIZE) {
      NotificationService.showResult(false, t('FEED_BACK.SMART_EDITOR.UPDATE.FAILURE.TOO_LARGE'));
      return;
    }

    // Update content and send to server
    setResourceContent(editorContent);
    updateResourceMutation.mutate({
      resourceId: resourceId,
      fileWithPath: file
    });

    // Invalidate raw text cache to reflect changes
    queryClient.invalidateQueries({
      queryKey: [RESOURCE_RAW_TEXT_QUERY_KEY, { resourceId }]
    });
  };

  const handleFileNameChange = (fileName: string) => {
    setPendingChanges(true);
    fileNameForm.setFieldValue('fileName', fileName);
    fileNameForm.validate();
  };

  if (downloadResourceContent.isLoading || !resourceContent || resourceInfoQuery.isLoading || !resource)
    return <FullScreenLoader />;
  if (downloadResourceContent.isError || resourceInfoQuery.isError) return <ErrorAlert />;

  return (
    <ResourceViewerWrapper resourceId={resourceId} fileName={fileNameForm.values.fileName} backgroundColor="white">
      <Flex w="100%" h="100%" align="center" justify="center" direction="column" style={{ backgroundColor: 'white' }}>
        <EditorHeader
          fileName={fileNameForm.values.fileName}
          resource={resource}
          isPendingChanges={isPendingChanges}
          isLoading={updateResourceMutation.isLoading}
          onFileNameChange={handleFileNameChange}
          onSaveChanges={handleSaveChanges}
        />
        <SmartEditor fileContent={resourceContent} setPendingChanges={setPendingChanges} ref={editorRef} />
      </Flex>
    </ResourceViewerWrapper>
  );
}
